import React, { useEffect } from "react";
import { Building2, ExternalLink, MapPin, ArrowDown } from "lucide-react";
import { company } from "../mock/mock";
import { PageHero } from "../components/Common";

const PARENT = "MIDATLANTIC BUSINESS MANAGEMENT PRIVATE LIMITED";

const brands = [
  {
    id: "midatlantic",
    name: "Mid Atlantic Business Management",
    url: "https://midatlantic-bps.com/",
    domain: "midatlantic-bps.com",
    type: "Corporate website of the company",
    desc: "Business process outsourcing, call centre, medical billing, debt collection, lead generation and B2B appointment-setting services.",
  },
  {
    id: "kohima-rentals",
    name: "Kohima Rentals",
    url: "https://kohimarentals.com/",
    domain: "kohimarentals.com",
    type: "Brand owned and operated by the company",
    desc: "Rental services brand serving customers in Kohima, Nagaland.",
  },
  {
    id: "kohima-taxi",
    name: "Kohima Taxi - Food Delivery",
    url: "https://kohimataxi.com/",
    domain: "kohimataxi.com",
    type: "Brand owned and operated by the company",
    desc: "Taxi booking and food delivery brand serving customers in Kohima, Nagaland.",
  },
];

const BrandCard = ({ b }) => (
  <div data-testid={`brand-card-${b.id}`} className="bg-white border border-border rounded-xl p-7 flex flex-col">
    <div className="section-eyebrow text-gold mb-2">{b.type}</div>
    <h3 className="font-heading font-bold text-navy text-xl">{b.name}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed mt-3 flex-1">{b.desc}</p>
    <p className="text-xs text-muted-foreground mt-4">Operated by: <span className="font-semibold text-navy">{PARENT}</span></p>
    <a
      data-testid={`brand-link-${b.id}`}
      href={b.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-5 inline-flex items-center gap-2 text-navy font-semibold text-sm hover:text-gold transition-colors"
    >
      {b.domain} <ExternalLink size={15} />
    </a>
  </div>
);

const OurBrands = () => {
  useEffect(() => {
    document.title = "Our Brands & Corporate Relationship | Midatlantic Business Management Pvt Ltd";
  }, []);

  return (
    <div data-testid="our-brands-page">
      <PageHero
        eyebrow="Corporate Information"
        title="Our Brands & Corporate Relationship"
        subtitle={`${PARENT} is the registered legal entity that owns and operates the websites and brands listed below.`}
      />
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div data-testid="parent-entity-card" className="brand-gradient rounded-2xl p-8 md:p-10 text-white">
            <div className="flex items-center gap-3 text-gold-light section-eyebrow mb-3">
              <Building2 size={18} /> Principal Entity
            </div>
            <h2 data-testid="parent-entity-name" className="font-heading font-extrabold text-2xl md:text-3xl">{PARENT}</h2>
            <p className="mt-4 text-white/80 leading-relaxed">
              The principal company and registered entity for DLT (Distributed Ledger Technology) SMS registration.
              All SMS communications sent under the brands below are sent by this entity.
            </p>
            <p data-testid="parent-entity-address" className="mt-5 flex items-start gap-2 text-sm text-white/85">
              <MapPin size={17} className="text-gold shrink-0 mt-0.5" /> {company.addressUS}
            </p>
          </div>

          <div className="flex justify-center py-6 text-gold"><ArrowDown size={28} /></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {brands.map((b) => <BrandCard key={b.id} b={b} />)}
          </div>

          <div data-testid="relationship-statement" className="mt-14 border-t border-border pt-10 space-y-4 text-muted-foreground leading-relaxed">
            <h2 className="font-heading font-bold text-navy text-xl">Relationship Statement</h2>
            <p>
              Mid Atlantic Business Management (midatlantic-bps.com), Kohima Rentals (kohimarentals.com) and
              Kohima Taxi - Food Delivery (kohimataxi.com) are brands of {PARENT}. They are not separate legal
              entities; each brand is owned, managed and operated by {PARENT}.
            </p>
            <p>
              For any verification related to these brands, contact us at{" "}
              <a href={`mailto:${company.email}`} className="text-navy font-semibold hover:text-gold">{company.email}</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurBrands;
